import { colors as themeColors } from '../utils/theme';
import { useState, useEffect, useRef } from 'react';
import { TextField, Paper, Table, TableHead, TableRow, TableCell, TableBody, CircularProgress, Typography, Box } from '@mui/material';
import axios from 'axios';

const DropdownProductCodeClientMaster = ({
    id,
    connectionString,
    companyId,
    AppRegID,
    value,
    onSelect,
    label = "Product Code",
    disabled = false,
    searchBy = 'code'
}) => {
    const [inputValue, setInputValue] = useState(value || '');
    const [dropdownOpen, setDropdownOpen] = useState(false);
    const [filteredOptions, setFilteredOptions] = useState([]);
    const [loading, setLoading] = useState(false);
    const dropdownRef = useRef(null);

    const requiredAsteriskStyle = {
        '& .MuiFormLabel-asterisk': {
            color: 'red',
        },
    };

    useEffect(() => {
        setInputValue(value || '');
    }, [value]);

    // Search products from API
    useEffect(() => {
        const searchProducts = async () => {
            if (!inputValue || inputValue.length < 1) {
                setFilteredOptions([]);
                setDropdownOpen(false);
                return;
            }

            try {
                setLoading(true);
                const apiUrl = `${connectionString}/api/partywise/searchProducts`;
                const params = {
                    searchTerm: inputValue,
                    companyId,
                    AppRegID,
                };

                console.log('🔍 Product Search API Call');
                console.log('URL:', apiUrl);
                console.log('Params:', params);

                const { data } = await axios.get(apiUrl, { params });

                console.log('✅ API Response:', data);

                if (Array.isArray(data) && data.length > 0) {
                    console.log('📊 API returned products:', data.length);
                    setFilteredOptions(data);
                    setDropdownOpen(true);
                } else {
                    console.log('📊 No results found from API');
                    setFilteredOptions([]);
                    setDropdownOpen(true);
                }
            } catch (error) {
                console.error('❌ Error searching products:', error);
                console.error('Error Details:', {
                    message: error.message,
                    response: error.response?.data,
                    status: error.response?.status,
                });
                setFilteredOptions([]);
            } finally {
                setLoading(false);
            }
        };

        const timeoutId = setTimeout(searchProducts, 300); // Debounce
        return () => clearTimeout(timeoutId);
    }, [inputValue, companyId, AppRegID, connectionString, searchBy]);

    // Click outside handler
    useEffect(() => {
        const handleClickOutside = (e) => {
            if (dropdownRef.current && !dropdownRef.current.contains(e.target)) {
                setDropdownOpen(false);
            }
        };
        document.addEventListener('mousedown', handleClickOutside);
        return () => document.removeEventListener('mousedown', handleClickOutside);
    }, []);

    const handleInputChange = (e) => {
        if (disabled) return;
        setInputValue(e.target.value);
        setDropdownOpen(true);
    };

    const handleSelect = (option) => {
        setInputValue(option.itemCode);
        setDropdownOpen(false);
        onSelect(option);
    };

    const handleKeyDown = (e) => {
        if (e.key === 'Enter' && filteredOptions.length) {
            handleSelect(filteredOptions[0]);
        }
    };

    return (
        <Box sx={{ position: 'relative' }} ref={dropdownRef}>
            <TextField
                fullWidth
                label={label}
                id={id}
                variant="outlined"
                value={inputValue}
                size="small"
                onChange={handleInputChange}
                disabled={disabled}
                InputLabelProps={{ sx: requiredAsteriskStyle }}
                onFocus={() => !disabled && setDropdownOpen(true)}
                onKeyDown={handleKeyDown}
                InputProps={{
                    endAdornment: loading ? <CircularProgress size={20} /> : null,
                }}
            />

            {dropdownOpen && inputValue.length >= 1 && (
                <Paper
                    elevation={3}
                    sx={{
                        position: 'absolute',
                        top: `calc(100% + 8px)`,
                        left: 0,
                        right: 0,
                        maxHeight: 400,
                        overflowY: 'auto',
                        zIndex: 1300,
                        minWidth: 300,
                    }}
                >
                    <Table size="small">
                        <TableHead>
                            <TableRow sx={{ backgroundColor: themeColors.neutral.border }}>
                                <TableCell sx={{ fontSize: '0.875rem' }}>
                                    <Typography fontWeight="bold">Item Code</Typography>
                                </TableCell>
                                <TableCell sx={{ fontSize: '0.875rem' }}>
                                    <Typography fontWeight="bold">Description</Typography>
                                </TableCell>
                                <TableCell sx={{ fontSize: '0.875rem' }}>
                                    <Typography fontWeight="bold">Sale Rate</Typography>
                                </TableCell>
                                <TableCell sx={{ fontSize: '0.875rem' }}>
                                    <Typography fontWeight="bold">Purchase Rate</Typography>
                                </TableCell>
                            </TableRow>
                        </TableHead>
                        <TableBody>
                            {loading ? (
                                <TableRow>
                                    <TableCell colSpan={4} align="center" sx={{ py: 2 }}>
                                        <CircularProgress size={30} />
                                    </TableCell>
                                </TableRow>
                            ) : filteredOptions.length > 0 ? (
                                filteredOptions.map((opt, idx) => (
                                    <TableRow
                                        hover
                                        key={idx}
                                        onClick={() => handleSelect(opt)}
                                        sx={{ cursor: 'pointer' }}
                                    >
                                        <TableCell sx={{ fontSize: '0.875rem' }}>{opt.itemCode}</TableCell>
                                        <TableCell sx={{ fontSize: '0.875rem' }}>{opt.description}</TableCell>
                                        <TableCell sx={{ fontSize: '0.875rem' }}>
                                            {Number(opt.saleRate || 0).toFixed(2)}
                                        </TableCell>
                                        <TableCell sx={{ fontSize: '0.875rem' }}>
                                            {Number(opt.purchaseRate || 0).toFixed(2)}
                                        </TableCell>
                                    </TableRow>
                                ))
                            ) : (
                                <TableRow>
                                    <TableCell colSpan={4} align="center" sx={{ py: 2 }}>
                                        <Typography color="text.secondary">No results found</Typography>
                                    </TableCell>
                                </TableRow>
                            )}
                        </TableBody>
                    </Table>
                </Paper>
            )}
        </Box>
    );
};

export default DropdownProductCodeClientMaster;
